/**
 * useScanner.js — QR Scanner hook'u
 * EmployeeAttendance'daki scanner mantığını yeniden kullanılabilir hale getirir
 */
import { useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

export default function useScanner({ regionId, t = (k, fallback) => fallback } = {}) {
  const scannerRef = useRef(null);
  const scanLockedRef = useRef(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerStatus, setScannerStatus] = useState('idle'); // idle | requesting | scanning | processing | success | error
  const [scannerMessage, setScannerMessage] = useState('');

  const stopScanner = useCallback(async () => {
    scanLockedRef.current = false;
    if (!scannerRef.current) return;

    try { await scannerRef.current.stop(); } catch { /* already stopped */ }
    try { await scannerRef.current.clear(); } catch { /* DOM cleanup */ }
    scannerRef.current = null;
  }, []);

  const closeScanner = useCallback(async () => {
    await stopScanner();
    setScannerOpen(false);
    setScannerStatus('idle');
    setScannerMessage('');
  }, [stopScanner]);

  const ensureCameraAccess = useCallback(async () => {
    const hasApi = typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia;
    if (!hasApi) throw new Error('CAMERA_API_NOT_SUPPORTED');

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
    } finally {
      stream?.getTracks().forEach((t) => t.stop());
    }
  }, []);

  const startScanner = useCallback(async (onScanSuccess) => {
    setScannerOpen(true);
    setScannerStatus('requesting');
    setScannerMessage(t('msg_requesting_camera', 'Kamera izni bekleniyor...'));

    try {
      await stopScanner();
      await ensureCameraAccess();

      const scanner = new Html5Qrcode(regionId, {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
      });
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 240, height: 240 },
          aspectRatio: 1,
          disableFlip: false,
        },
        async (decodedText) => {
          if (scanLockedRef.current) return;
          scanLockedRef.current = true;
          setScannerStatus('processing');
          setScannerMessage(t('msg_qr_detected', 'QR algılandı, doğrulanıyor...'));
          await onScanSuccess(decodedText);
        },
        () => { /* ignore continuous errors */ }
      );

      setScannerStatus('scanning');
      setScannerMessage(t('msg_scan_qr_prompt', 'QR kodu kameraya gösterin.'));
    } catch (error) {
      const msg = String(error?.message || t('err_camera_qr', 'Kamera hatası.'));
      setScannerStatus('error');
      setScannerMessage(msg);
      await stopScanner();
    }
  }, [regionId, t, stopScanner, ensureCameraAccess]);

  const markSuccess = useCallback((message) => {
    setScannerStatus('success');
    setScannerMessage(message);
    setTimeout(() => closeScanner(), 700);
  }, [closeScanner]);

  const markError = useCallback((message) => {
    setScannerStatus('error');
    setScannerMessage(message);
    stopScanner();
  }, [stopScanner]);

  return {
    scannerOpen,
    scannerStatus,
    scannerMessage,
    scanLockedRef,
    startScanner,
    closeScanner,
    stopScanner,
    markSuccess,
    markError,
  };
}